import { registerIntegration } from "@p0security/shared/integrations/registry";
import {
  type AnyFilterSchema,
  Registration,
} from "@p0security/shared/integrations/types";

import { ArgocdAccesses } from "./accesses.ts";
import { ArgocdComponents } from "./components.ts";

// Rule filter values compile to regular expressions, so a rule can say, e.g.,
// that `role:admin` needs approval. `instance` matches the instance's item
// id. `project` applies to `project-role` only.
const ArgocdFilters = {
  instance: { keys: ["name"] },
  project: { keys: ["name"] },
  role: { keys: ["name"] },
} as const satisfies AnyFilterSchema;
export type ArgocdFilters = typeof ArgocdFilters;

// Keyed by component, so a new component doesn't compile until it has one.
const ITEM_KINDS: Record<keyof typeof ArgocdComponents, string> = {
  connector: "Connector",
  "argocd-instance": "Argo CD instance",
};
// `Registration` hands `itemKind` any string, not just a component key.
const itemKinds: Partial<Record<string, string>> = ITEM_KINDS;

export const ArgocdRegistration = Registration({
  accesses: ArgocdAccesses,
  category: "resource",
  components: ArgocdComponents,
  docs: {
    install: "argocd/installation",
    request: "argocd/requesting-access",
  },
  filters: ArgocdFilters,
  // An item's id is P0's own name for it. "Name" would read as the connector
  // name that `hosting` asks for.
  identifier: "ID",
  itemKind: (component) => itemKinds[component] ?? component,
  label: "Argo CD",
  logoUrl: "https://argo-cd.readthedocs.io/en/stable/assets/logo.png",
  maturity: "beta",
  requestType: "requestable",
  // A connector alone grants nothing, so Argo CD counts as installed once an
  // instance is.
  useMapping: [{ use: "iam_write", component: "argocd-instance" }],
});

registerIntegration("argocd", ArgocdRegistration);
